(() => {
  const DAY = 86400000;
  const MAX_TIMEOUT = 2147483647;
  const prefix = 'settle-welcome-offer:';

  function expiryTime(value) {
    if (!value) return Infinity;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value || '')) return NaN;
    return Date.parse(value);
  }

  function shouldShow(now, expires, lastSeen, repeatDays) {
    return (Number.isFinite(expires) || expires === Infinity) && now < expires &&
      (!Number.isFinite(lastSeen) || lastSeen > now || now - lastSeen >= repeatDays * DAY);
  }

  function readSeen(key) {
    try {
      const value = Number(window.localStorage.getItem(key));
      return value > 0 ? value : NaN;
    } catch (_) { return NaN; }
  }

  function saveSeen(key) {
    try { window.localStorage.setItem(key, String(Date.now())); } catch (_) { }
  }

  function isPassiveBanner(popup) {
    return popup.classList.contains('notification-banner') &&
      popup.dataset.openTrigger === 'delay' &&
      popup.dataset.overlay === 'false' &&
      popup.dataset.bodyScrollDisable === 'false' &&
      !popup.querySelector('.popup-button, .popup-close, .newsletter-form');
  }

  function hasBlockingPopup() {
    return Array.from(document.querySelectorAll('.popup-open')).some(popup => !isPassiveBanner(popup));
  }

  function init(dialog) {
    if (dialog.dataset.welcomeInitialized === 'true') return;
    dialog.dataset.welcomeInitialized = 'true';
    const expires = expiryTime(dialog.dataset.expiryUtc);
    const repeatDays = Math.max(1, Number(dialog.dataset.repeatDays) || 7);
    const delay = Math.max(0, Number(dialog.dataset.delaySeconds) || 0) * 1000;
    const key = prefix + dialog.dataset.campaign;
    const editor = Boolean(window.Shopify && window.Shopify.designMode);
    let timer;
    let expiryTimer;
    let previousFocus;
    let waitingForPopup;
    let pendingEditorPreview = false;
    let unloaded = false;
    let scrollLock;
    const closeButton = dialog.querySelector('[data-welcome-close]');

    function lockBackground() {
      const x = window.scrollX;
      const y = window.scrollY;
      const changes = [
        [document.documentElement, 'overflow', 'hidden'],
        [document.body, 'overflow', 'hidden'],
        [document.body, 'position', 'fixed'],
        [document.body, 'top', `-${y}px`],
        [document.body, 'left', `-${x}px`],
        [document.body, 'width', '100%']
      ];
      scrollLock = {
        x, y,
        changes: changes.map(([element, property, value]) => {
          const previous = element.style.getPropertyValue(property);
          const priority = element.style.getPropertyPriority(property);
          element.style.setProperty(property, value);
          return { element, property, value, previous, priority };
        })
      };
    }

    function unlockBackground() {
      if (!scrollLock) return;
      const { x, y, changes } = scrollLock;
      scrollLock = undefined;
      for (const { element, property, value, previous, priority } of changes) {
        if (element.style.getPropertyValue(property) !== value) continue;
        if (previous) element.style.setProperty(property, previous, priority);
        else element.style.removeProperty(property);
      }
      window.scrollTo(x, y);
    }

    function remember() { if (!editor) saveSeen(key); }
    function close() { if (dialog.open) dialog.close(); }
    function expire() {
      if (Date.now() >= expires) { close(); return; }
      expiryTimer = window.setTimeout(expire, Math.min(expires - Date.now(), MAX_TIMEOUT));
    }
    function stopWaiting() {
      if (waitingForPopup) waitingForPopup.disconnect();
      waitingForPopup = undefined;
    }
    function show(forceEditorPreview = false) {
      if (unloaded || dialog.open || Number.isNaN(expires) || Date.now() >= expires) return;
      if (!forceEditorPreview && !editor && !shouldShow(Date.now(), expires, readSeen(key), repeatDays)) return;
      if (hasBlockingPopup()) {
        pendingEditorPreview = pendingEditorPreview || forceEditorPreview;
        if (!waitingForPopup) {
          waitingForPopup = new MutationObserver(() => {
            if (!hasBlockingPopup()) {
              const force = pendingEditorPreview;
              pendingEditorPreview = false;
              stopWaiting();
              show(force);
            }
          });
          waitingForPopup.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });
        }
        return;
      }
      stopWaiting();
      previousFocus = document.activeElement;
      dialog.showModal();
      lockBackground();
      document.body.classList.add('welcome-offer-active');
      if (Number.isFinite(expires)) expire();
    }
    closeButton.addEventListener('click', close);
    const openLink = event => {
      const link = event.target.closest('a[href="#settle-newsletter"]');
      if (!link) return;
      event.preventDefault();
      previousFocus = link;
      show(true);
    };
    document.addEventListener('click', openLink);
    dialog.addEventListener('click', event => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', () => {
      window.clearTimeout(expiryTimer);
      document.body.classList.remove('welcome-offer-active');
      remember();
      if (previousFocus && previousFocus.isConnected && previousFocus.focus) previousFocus.focus({ preventScroll: true });
      unlockBackground();
    });
    if (dialog.querySelector('[data-welcome-result]')) {
      show(true);
    } else if (!editor && shouldShow(Date.now(), expires, readSeen(key), repeatDays)) {
      timer = window.setTimeout(() => show(), delay);
    }
    dialog.addEventListener('settle:preview', () => show(true));
    dialog.addEventListener('settle:deselect', () => {
      if (editor) { stopWaiting(); close(); }
    });
    dialog.addEventListener('settle:unload', () => {
      unloaded = true;
      document.removeEventListener('click', openLink);
      stopWaiting();
      window.clearTimeout(timer);
      window.clearTimeout(expiryTimer);
      close();
      unlockBackground();
    });
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-welcome-offer]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  if (document.readyState !== 'loading') initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:select', event => {
    const dialog = event.target.querySelector('[data-welcome-offer]');
    if (dialog) { init(dialog); dialog.dispatchEvent(new Event('settle:preview')); }
  });
  document.addEventListener('shopify:section:deselect', event => {
    const dialog = event.target.querySelector('[data-welcome-offer]');
    if (dialog) dialog.dispatchEvent(new Event('settle:deselect'));
  });
  document.addEventListener('shopify:section:unload', event => {
    const dialog = event.target.querySelector('[data-welcome-offer]');
    if (dialog) dialog.dispatchEvent(new Event('settle:unload'));
  });
  window.SettleWelcomeOffer = { expiryTime, shouldShow };
})();
